import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import PERM from 'constants/core/perm.const';
import useIsPersonalCalendarOnly from 'hooks/useIsPersonalCalendarOnly.hook';
import { useCustomPermission } from 'providers/Access.provider';
import { selectAccessHasLockToken } from 'selectors/access.selectors';

/**
 * Appointment permission flags.
 *
 * The backend assigns the default permission IDs to every stylist and only
 * includes a permission ID in the lock token when the value is granted
 * (`all_staff` / `personal` for radio permissions, `true` for boolean
 * permissions). A `no_access` or `false` value is represented by the
 * permission ID being absent from the token, so we treat any missing ID as
 * "denied" for any user that has a lock token at all.
 *
 * When there is no lock token (e.g. the master/manager session without a
 * PIN), permissions are not applied and every check defaults to allowed.
 */
export const useAppointmentPermissions = () => {
  const hasLockToken = useSelector(selectAccessHasLockToken);

  const canCreateAll = useCustomPermission(PERM.canCreateAppointmentAllStaff);
  const canCreatePersonal = useCustomPermission(PERM.canCreateAppointmentPersonal);

  const canMoveAll = useCustomPermission(PERM.canMoveAppointmentAllStaff);
  const canMovePersonal = useCustomPermission(PERM.canMoveAppointmentPersonal);

  const canDeleteAll = useCustomPermission(PERM.canDeleteAppointmentAllStaff);
  const canDeletePersonal = useCustomPermission(PERM.canDeleteAppointmentPersonal);

  const canManageSlideoutAll = useCustomPermission(PERM.canManageSlideoutAppointmentsAllStaff);
  const canManageSlideoutPersonal = useCustomPermission(PERM.canManageSlideoutAppointmentsPersonal);

  const rawCanEditPrice = useCustomPermission(PERM.canEditAppointmentPrice);
  const rawCanEditDuration = useCustomPermission(PERM.canEditAppointmentDuration);

  // Privacy / "Time booked" masking for other stylists' appointments is
  // driven by the VIEW permission, not by create/move/delete. Reuses the
  // shared hook so scheduler gating and appointment privacy stay in sync.
  // See GS-6088.
  const { isPersonalOnly: isPersonalCalendarOnly, accessStylistId } = useIsPersonalCalendarOnly();

  return useMemo(
    () => ({
      accessStylistId,

      isCreateBlocked: hasLockToken && !canCreateAll && !canCreatePersonal,
      isCreatePersonalOnly: canCreatePersonal && !canCreateAll,

      isMoveBlocked: hasLockToken && !canMoveAll && !canMovePersonal,
      isMovePersonalOnly: canMovePersonal && !canMoveAll,

      isDeleteBlocked: hasLockToken && !canDeleteAll && !canDeletePersonal,
      isDeletePersonalOnly: canDeletePersonal && !canDeleteAll,

      isSlideoutBlocked: hasLockToken && !canManageSlideoutAll && !canManageSlideoutPersonal,
      isSlideoutPersonalOnly: hasLockToken && canManageSlideoutPersonal && !canManageSlideoutAll,
      canManageSlideoutAll,

      canCreateAll,
      canMoveAll,
      canDeleteAll,
      isPersonalCalendarOnly,

      canEditPrice: !hasLockToken || rawCanEditPrice,
      canEditDuration: !hasLockToken || rawCanEditDuration,
    }),
    [
      accessStylistId,
      canCreateAll,
      canCreatePersonal,
      canDeleteAll,
      canDeletePersonal,
      canManageSlideoutAll,
      canManageSlideoutPersonal,
      canMoveAll,
      canMovePersonal,
      isPersonalCalendarOnly,
      hasLockToken,
      rawCanEditDuration,
      rawCanEditPrice,
    ],
  );
};
